import React, { useState, useEffect, useRef } from 'react';
import { getTheme, useThemeMode } from '../theme';
import type { FastighetStatus } from '../types';

export function FastighetMiniMap({ beteckning, onAutoFill }: { beteckning: string; onAutoFill?: (fields: { municipality?: string; county?: string; areaHa?: string }) => void }) {
  const { mode } = useThemeMode(); const T = getTheme(mode);
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const [status, setStatus] = useState<FastighetStatus>('idle');
  const [coords, setCoords] = useState<[number, number] | null>(null);

  /* Debounced geocoding */
  useEffect(() => {
    const b = beteckning.trim();
    if (!b || !/\d+:\d+/.test(b)) { setStatus('idle'); setCoords(null); return; }
    const t = setTimeout(async () => {
      setStatus('searching');
      try {
        /* Parse tract/municipality text before the "X:Y" number */
        const m = b.match(/^(.+?)\s+\d+:\d+$/);
        const q = (m ? m[1].trim() : b) + ' Sverige';
        const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1&countrycodes=se&addressdetails=1`, { headers: { 'Accept-Language': 'sv', 'User-Agent': 'Qvenari/1.0' } });
        const d = await r.json();
        if (!d.length) { setStatus('notfound'); return; }
        const lat = parseFloat(d[0].lat), lng = parseFloat(d[0].lon);
        /* Auto-fill municipality/county if callback provided */
        if (onAutoFill) {
          const addr = d[0].address || {};
          onAutoFill({
            municipality: addr.city || addr.town || addr.municipality || undefined,
            county: addr.county || addr.state || undefined,
          });
        }
        setCoords([lat, lng]);
        setStatus('found');
      } catch { setStatus('notfound'); }
    }, 900);
    return () => clearTimeout(t);
  }, [beteckning]);

  /* Leaflet map init when coords arrive */
  useEffect(() => {
    if (!coords || status !== 'found' || !mapDivRef.current) return;
    let alive = true;
    (async () => {
      const L = (await import('leaflet')).default;
      await import('leaflet/dist/leaflet.css');
      if (!alive || !mapDivRef.current) return;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
      await new Promise(r => setTimeout(r, 60));
      if (!alive || !mapDivRef.current) return;
      const [lat, lng] = coords;
      const map = L.map(mapDivRef.current, { zoomControl: true, attributionControl: false }).setView([lat, lng], 14);
      mapRef.current = map;
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
      /* Lantmäteriet property boundaries (no API key needed for tile rendering) */
      (L as any).tileLayer.wms('https://minkarta.lantmateriet.se/map/fastighetsindelning?', { layers: 'granser', format: 'image/png', transparent: true, opacity: 0.95, maxZoom: 19 }).addTo(map);
      (L as any).tileLayer.wms('https://minkarta.lantmateriet.se/map/fastighetsindelning?', { layers: 'text', format: 'image/png', transparent: true, opacity: 0.9, maxZoom: 19 }).addTo(map);
      L.marker([lat, lng]).addTo(map);
      setTimeout(() => { map.invalidateSize(); }, 120);
    })();
    return () => {
      alive = false;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [coords, status]);

  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);

  if (status === 'idle') return null;
  return (
    <div style={{ marginTop: 8 }}>
      {status === 'searching' && <div style={{ color: T.textMuted, fontSize: 13, fontStyle: 'italic', padding: '6px 0' }}>Söker fastighetsbeteckning...</div>}
      {status === 'notfound' && <div style={{ color: '#e57373', fontSize: 13, padding: '6px 0' }}>Hittades inte — kontrollera stavningen (t.ex. "Falun Rökärr 1:1")</div>}
      {status === 'found' && (
        <>
          <div style={{ color: '#4caf50', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>✓ Hittad — kontrollera att rätt fastighet syns på kartan</div>
          <div ref={mapDivRef} style={{ height: 220, borderRadius: 6, border: `1px solid ${T.border}`, overflow: 'hidden' }} />
          <div style={{ color: T.textMuted, fontSize: 11, marginTop: 4 }}>Fastighetsgränser © Lantmäteriet. Zooma och panorera för att hitta rätt fastighet.</div>
        </>
      )}
    </div>
  );
}
